import { CounselingTypeEnum } from 'app/Bookings/constants'
import { getFilterValueFromId } from 'app/ProviderDetails/utils'
import HeaderFilter from 'app/ProviderSearch/HeaderFilter'
import ProviderFilter from 'app/ProviderSearch/ProviderFilter'
import ProvidersList from 'app/ProviderSearch/ProvidersAndMap/ProvidersList'
import ProvidersListWaterfall from 'app/ProviderSearch/ProvidersAndMap/ProvidersListWaterfall'
import { AvailabilityFilterValues } from 'app/ProviderSearch/constants'
import { getFilterLookup, groupFilters } from 'app/ProviderSearch/utils'
import classNames from 'classnames'
import AvailabilityLegend from 'common/AvailabilityLegend'
import Navbar from 'common/Navbar'
import PageLoader from 'common/PageLoader'
import Prenav from 'common/Prenav'
import ResetSupportBooking from 'common/ResetSupportBooking'
import ScrollHorizontally from 'common/ScrollHorizontally'
import ShowHideMapButton from 'common/ShowHideMapButton'
import HeadComponent from 'components/common/HeadComponent'
import { PAGINATION_LIMIT } from 'constants/api'
import {
  LOCATION_FETCH_STATUS,
  SLOTS_ROW_LIMIT_V3,
  sessionStorageKeys,
  supportDashboardToProviderSearchKeys,
  searchNumber,
} from 'constants/common'
import { usedForFeature } from 'constants/logEvents'
import { RudderEvent } from 'constants/rudderEvents'
import ProviderSlotContext, {
  getProviderSlots,
} from 'context/ProviderBooking/ProviderSlots'
import SlotDateContext, { slotDate } from 'context/ProviderBooking/SlotDate'
import MobileFilterModalProvider from 'context/ProviderSearch/MobileFilterModalProvider'
import InsuranceProvider from 'context/common/InsuranceProvider'
import useTimeZoneContext, {
  TimeZoneContext,
} from 'context/common/TimeZoneContext'
import { ProviderFilterTypes, getFilters } from 'dataSource/ProviderSearch'
import useSalesforceLead from 'hooks/salesforce/useSalesforceLead'
import useShownSlots from 'hooks/slots/useShownSlots'
import useB2bDetailsByKeyword from 'hooks/useB2bDetailsByKeyword'
import useBreakpoint from 'hooks/useBreakPoint'
import useFetchLocation from 'hooks/useFetchLocation'
import useHeaderScrollAnimate from 'hooks/useHeaderScrollAnimate'
import useInitGlobalFilter from 'hooks/useInitGlobalFilter'
import useThriveAuth from 'hooks/useThriveAuth'
import useCanonicalLink from 'hooks/useCanonicalLink'
import _isEmpty from 'lodash/isEmpty'
import _omit from 'lodash/omit'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { animated } from 'react-spring'
import { getParsedQuery, hashSfAddress, modifyURLQuery } from 'utils/common'
import logEvent from 'utils/logEvent'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import { hasSecondaryFilters } from 'app/ProviderSearch/utils'
import styles from './index.module.scss'
import useSalesforceCase from 'hooks/salesforce/useSalesforceCase'
import useGetUserTreatment from 'hooks/treatments/useGetUserTreatment'
import SimpleInsuranceVerification from 'common/SimpleInsuranceVerification'
import { updateProviderSearchQueryUrl } from 'common/SimpleInsuranceVerification/updateProviderSearchQueryUrl'

const Footer = dynamic(() => import('components/common/Footer'))

const ChangeLocationModal = dynamic(
  () => import('components/app/ProviderSearch/ChangeLocationModal')
)

const PracticesMap = dynamic(
  () => import('app/ProviderSearch/ProvidersAndMap/PracticesMap'),
  {
    ssr: false,
    loading: () => (
      // Placeholder the size of the map until map isn't loaded
      <div className="available-page-height flex-fill position-sticky top-0 app-border-radius"></div>
    ),
  }
)

const SearchPage: React.FC<ProvidersListingPageProps> = ({
  groupedFilters: initialGroupedFilters,
  filterLookup: initialFilterLookup,
}) => {
  const { isSupportUser, isSupportOrThirdPartyUser } = useThriveAuth()
  const [showMobileFilterModal, setShowMobileFilterModal] = useState<boolean>(
    false
  )
  const [groupedFilters, setGroupedFilters] = useState<GroupedProviderFilter>(
    initialGroupedFilters
  )
  const [filterLookup, setFilterLookup] = useState<ProviderFilterLookup>(
    initialFilterLookup
  )
  const [shouldMapLoad, setShouldMapLoad] = useState<boolean>(false)
  const [providerListLoading, setProviderListLoading] = useState<boolean>(true)
  const { phoneNumber, formattedPhoneNumber } = searchNumber

  // this variable is to handle zero provider state cases in waterfall flow
  const [isZeroProviderState, setIsZeroProviderState] = useState<boolean>(false)
  const [
    waterfallProviderInsuranceInfo,
    setWaterfallProviderInsuranceInfo,
  ] = useState<WaterfallProvidersInsuranceNWInfo>({
    noWaterfallProviderAcceptsInsurance: false,
    notInNWProvidersCount: 0,
  })

  const isDirectMatch0WithOnlyPrimaryFilter = useRef(false)
  // ===================================== //
  const previousLocationState = useRef<string>('')
  const previousSalesforceLocationHash = useRef<string>('')
  const previousPartnershipCode = useRef<string>('')
  const router = useRouter()
  const { asPath } = router
  const query = getParsedQuery(router)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  //Hiding the shadow on page scroll due to THRIV-3267, can come back so commenting this instead
  //const [pageScrolled, setPageScrolled] = useState<boolean>(false)
  const [providerList, setProviderList] = useState<Provider[]>([])
  const [providerPagination, setProviderPagination] = useState<PaginationType>({
    count: 0,
    offset: 0,
  })
  // TODO move the practice related logic and state management logic to the PracticeMaps component
  const [practicesList, setPracticesList] = useState<PracticeProvider[]>([])
  const [selectedPractice, setSelectedPractice] = useState<PracticeProvider>(
    null
  )
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false)
  useInitGlobalFilter(initialGroupedFilters)
  /*
    useB2bDetailsByKeyword will fetch the b2b details if there is any partner
    related keyword in url and set the partnership details for the end user
  */
  useB2bDetailsByKeyword()
  const { timeZone, format, ...restTimeZone } = useTimeZoneContext()
  const {
    startSlotDate,
    slotListLimit,
    setStartSlotDate,
    setSlotListLimit,
  } = slotDate(timeZone)

  const {
    providerSlots,
    loading: slotsLoading,
    error: slotsError,
    nextAvailabilityForProviders,
  } = getProviderSlots({
    startSlotDate,
    slotListLimit,
    providerList,
    timeZone,
    availabilityFilters: groupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE],
  })

  const {
    lead,
    leadId,
    isFetched: isLeadFetched,
    isSalesForceLeadFetchEnabled,
  } = useSalesforceLead()

  const { sfAccount, caseId, hasCaseId } = useSalesforceCase()
  const canonicalLink = useCanonicalLink(groupedFilters)

  const isSalesForceB2BOn =
    useGetTreatment(TreatmentFlags.SALESFORCE_B2B_CASE_ACCOUNT) ===
    treatmentFlagValues.ON
  const sfCaseAutoFill =
    useGetTreatment(TreatmentFlags.SALESFORCE_CASE_AUTOFILL) ===
    treatmentFlagValues.ON

  const showWaterFallTreatment = useMemo(() => {
    if (
      !providerListLoading &&
      providerPagination?.count < 10 &&
      !isDirectMatch0WithOnlyPrimaryFilter.current &&
      hasSecondaryFilters(getParsedQuery(router))
    ) {
      return true
    }

    return false
  }, [
    providerListLoading,
    providerPagination,
    isDirectMatch0WithOnlyPrimaryFilter.current,
    router,
  ])

  // When latitude or longitude is not present but location string is present
  // Then use that string to get the latitude and longitude
  const fetchLocationEnabled = useMemo(() => {
    const { location, latitude, longitude, state } = getParsedQuery(router)
    return !(location && latitude && longitude && state)
  }, [asPath])

  const {
    locationData,
    loading: locationFetchLoading,
    status: locationFetchStatus,
  } = useFetchLocation({
    locationString: query.location as string,
    fetchLocationEnabled,
    usedForFeature:
      usedForFeature.SearchPageAutoSelectCurrentLocationOrFromQuery,
  })

  const { isCustomSize: showMapOnDesktopScreen } = useBreakpoint({
    minWidth: 1080,
  })
  const {
    isMobileScreen,
    isLargeSize,
    isExtraLargeSize,
    isAboveXLSize,
  } = useBreakpoint()
  const isDesktopView = isExtraLargeSize || isAboveXLSize

  const headerPositionStyle = useHeaderScrollAnimate({
    animation: {
      hide: 'translateY(-223px)',
      show: 'translateY(0px)',
    },
  })

  //=============================================================Use Effects
  useEffect(() => {
    // Save the current param

    const currentSearchQuery = getParsedQuery(router)
    const previousSearchedQuery = JSON.parse(
      sessionStorage.getItem(sessionStorageKeys.PROVIDER_SEARCH_QUERY)
    )
    const isSalesforceCase = hasCaseId
    const previousCode = previousSearchedQuery?.partnershipCode || ''
    const SalesforceCase = isSalesforceCase ? '' : previousCode

    previousPartnershipCode.current = isSalesForceB2BOn
      ? SalesforceCase
      : previousCode

    sessionStorage.setItem(
      sessionStorageKeys.PROVIDER_SEARCH_QUERY,
      JSON.stringify(
        _omit(currentSearchQuery, [
          ...supportDashboardToProviderSearchKeys,
          !isSalesForceLeadFetchEnabled && 'leadId',
        ])
      )
    )
  }, [asPath])

  useEffect(() => {
    const qsQuery = getParsedQuery(router)
    const queryState = qsQuery.state

    const SfAddress =
      lead?.Address || (sfCaseAutoFill && sfAccount?.PersonMailingAddress)
    const salesforceLocationHash = SfAddress ? hashSfAddress(SfAddress) : ''
    const hasSalesforceAddress = SfAddress
    let salesforceAddressChanged =
      hasSalesforceAddress &&
      previousSalesforceLocationHash.current &&
      previousSalesforceLocationHash.current !== salesforceLocationHash

    if (hasSalesforceAddress && !previousSalesforceLocationHash.current) {
      previousSalesforceLocationHash.current = salesforceLocationHash
    }

    if (
      sfCaseAutoFill &&
      sfAccount?.PersonMailingAddress &&
      salesforceAddressChanged
    ) {
      salesforceAddressChanged = false
    }

    const userSetLocationChanged =
      qsQuery.state &&
      previousLocationState.current !== queryState &&
      previousLocationState.current !== ''

    if (userSetLocationChanged) {
      filtersAPICall(queryState)
    }

    previousLocationState.current = queryState

    /*
     * If either of location, state, lat, long is missing
     * if its present then it would take the value from the session storage and use it in the queryParams
     * else it would show the location popup
     */

    const isQueryLocationDataIncomplete =
      !qsQuery?.location ||
      !qsQuery?.state ||
      !qsQuery?.latitude ||
      !qsQuery?.longitude

    const individualCounselingFilter = groupedFilters[
      ProviderFilterTypes.COUNSELING_TYPE
    ]?.find((filter) => filter.value === CounselingTypeEnum.INDIVIDUAL)
    let counselingTypeFilter: string[] = null

    if (!_isEmpty(qsQuery.counselingType)) {
      counselingTypeFilter = qsQuery.counselingType as string[]
    } else if (individualCounselingFilter?.id) {
      counselingTypeFilter = [individualCounselingFilter.id]
    }

    const modifiedQuery = _omit(qsQuery, [
      'latitude',
      'longitude',
      'state',
      'location',
      'userId',
      'patients',
    ])

    if (
      !!previousPartnershipCode.current &&
      !qsQuery.partnershipCode &&
      _isEmpty(qsQuery)
    ) {
      modifyURLQuery(router, {
        partnershipCode: previousPartnershipCode.current,
      })
    }

    // If the Salesforce lead address has changed and the user has not manually changed the location,
    // then update the provider search with the new Salesforce lead address.
    // hide the location modal if it is open since we have a location

    if (salesforceAddressChanged && !userSetLocationChanged) {
      previousSalesforceLocationHash.current = salesforceLocationHash
      const newQuery = {
        ...modifiedQuery,
        state: SfAddress?.state,
        latitude: SfAddress?.latitude?.toString(),
        longitude: SfAddress?.longitude?.toString(),
        location: SfAddress?.city ?? SfAddress?.state ?? SfAddress?.postalCode,
      }
      modifyURLQuery(
        router,
        {
          ...newQuery,
          limit: PAGINATION_LIMIT.toString(),
          offset: modifiedQuery.offset || '0',
          ...(qsQuery.userId && { userId: qsQuery.userId }),
          ...(qsQuery.patients && { patients: qsQuery.patients }),
          ...(counselingTypeFilter && {
            counselingType: counselingTypeFilter,
          }),
        },
        true,
        'replace'
      )

      setShowLocationModal(false)
      return
    }

    // If the location data in the query is incomplete (i.e., either the location, state, latitude, or longitude is missing),
    // this block of code will attempt to complete the missing data.
    // There are three scenarios handled here:
    // 1. If the latitude and longitude are missing but we have location data available,
    //    the code will use the user's current location data to fill in the missing latitude, longitude, state, and location.
    // 2. If there are no query parameters related to location, the code will check for previously stored parameters
    //    and use those to fill in the missing data.
    // 3. If neither of the above conditions are met, the function will return without making any changes.
    // After determining the new query parameters, the code will call the `modifyURLQuery` function to update the URL
    // with the new query parameters. This will trigger a new search with the updated location data.
    // The user experience here is that if they have incomplete location data in their search,
    // the application will attempt to automatically complete it using either their current location or previously stored data.
    // If neither is available, the search will not be performed and the user will need to manually input the location data.

    if (isQueryLocationDataIncomplete && !locationFetchLoading) {
      if (locationData) {
        // WHen a location query is set without other parameter
        // Displays from users current location data
        const newQuery = {
          ...modifiedQuery,
          latitude: locationData.lat,
          longitude: locationData.lng,
          state: locationData.state,
          location: locationData.formatted_address,
        }

        modifyURLQuery(
          router,
          {
            ...newQuery,
            limit: PAGINATION_LIMIT.toString(),
            offset: modifiedQuery.offset || '0',
            ...(qsQuery.userId && { userId: qsQuery.userId }),
            ...(qsQuery.patients && { patients: qsQuery.patients }),
            ...(counselingTypeFilter && {
              counselingType: counselingTypeFilter,
            }),
          },
          true,
          'replace'
        )
        return
      }

      if (hasSalesforceAddress) {
        return
      }

      setShowLocationModal(true)
    }
  }, [
    asPath,
    isLeadFetched,
    locationData,
    locationFetchLoading,
    lead?.Address,
    sfAccount?.PersonMailingAddress,
  ])

  //============================================================= Filters Api call
  const filtersAPICall = async (state?: string | null) => {
    const response = await getFilters(state)
    const groupedFilters = groupFilters(response.result)
    const filterLookup = getFilterLookup(response.result)

    setGroupedFilters(groupedFilters)
    setFilterLookup(filterLookup)
  }

  //============================================================= Page functions
  const onChangeLocation = (locationData: ChangeLocationFormProps) => {
    const { partnershipCode, counselingType, ...restQuery } = getParsedQuery(
      router
    )
    const { location, state, latitude, longitude } = locationData
    const allCounselingTypeFilters =
      groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]
    const isCounselingTypeInFilters = allCounselingTypeFilters?.find(
      (filter) => filter.id === counselingType?.[0]
    )
    const individualCounselingFilter = groupedFilters[
      ProviderFilterTypes.COUNSELING_TYPE
    ]?.find((filter) => filter.value === CounselingTypeEnum.INDIVIDUAL)
    const counselingTypeId = isCounselingTypeInFilters
      ? counselingType?.[0]
      : individualCounselingFilter.id
    const newQuery = {
      ...restQuery,
      state,
      latitude,
      longitude,
      location,
      ...(isSalesForceB2BOn
        ? { sfId: leadId ? leadId : caseId || undefined }
        : { leadId: leadId ? leadId : undefined }),
    }

    modifyURLQuery(
      router,
      {
        ...newQuery,
        limit: PAGINATION_LIMIT.toString(),
        offset: '0',
        ...(counselingTypeId && {
          counselingType: [counselingTypeId],
        }),
        ...(!!partnershipCode && { partnershipCode }),
        ...(isSalesForceB2BOn
          ? query?.sfId && isSalesForceLeadFetchEnabled && { sfId: query.sfId }
          : query?.leadId &&
            isSalesForceLeadFetchEnabled && { leadId: query.leadId }),
      },
      true,
      'replace'
    )
  }

  //=============================================================

  // Hiding the shadow on page scroll due to THRIV-3267, can come back so commenting this instead
  // const onProviderListScroll = ({ target }) => {
  //   const { scrollTop } = target

  //   if (scrollTop > 0 && !pageScrolled) {
  //     setPageScrolled(true)
  //   } else if (scrollTop === 0 && pageScrolled) {
  //     setPageScrolled(false)
  //   }
  // }

  const isEmptyQuery = _isEmpty(query)

  const isInPersonSelected = useMemo(() => {
    const availabilityFilterQuery = query['availabilityType[]']
    const inPersonFilter = initialGroupedFilters[
      ProviderFilterTypes.AVAILABILITY_TYPE
    ].find((filter) => filter.value === AvailabilityFilterValues.inPerson)

    return availabilityFilterQuery === inPersonFilter.id
  }, [query, initialGroupedFilters])
  const slotDateValue: SlotDateType = {
    startSlotDate,
    slotListLimit,
    setStartSlotDate,
    setSlotListLimit,
  }

  const providerSlotValue: ProviderScheduleDetails = {
    nextAvailabilityForProviders,
    providerSlots,
    loading: slotsLoading,
    error: slotsError,
  }

  const { countDetails, providerCardDetails } = useShownSlots(
    SLOTS_ROW_LIMIT_V3,
    providerList || [],
    slotDateValue,
    providerSlotValue,
    timeZone
  )
  const userSelectedFilters = getFilterValueFromId(groupedFilters, query)

  const logMapRudderstackEvent = (shouldMapLoad?: boolean) => {
    const eventName = shouldMapLoad
      ? RudderEvent.BOOKING_TOOL_SHOW_MAP_CLICKED
      : RudderEvent.BOOKING_TOOL_HIDE_MAP_CLICKED
    logEvent(eventName, {
      filters: {
        age: userSelectedFilters?.client_age_group || 'null',
        appointment_type: userSelectedFilters?.availability_type || 'null',
        counseling_approach: userSelectedFilters?.counseling_approach || 'null',
        gender: userSelectedFilters?.gender || 'null',
        issues: userSelectedFilters?.issues || 'null',
        language: userSelectedFilters?.language || 'null',
        payment_type: query.insuranceId
          ? sessionStorage.getItem(sessionStorageKeys.INSURANCE_PROVIDER_NAME)
          : 'Self Pay',
        therapy_type: userSelectedFilters?.counseling_type || 'null',
      },
      total_providers_available: providerCardDetails?.length,
      count_provider_slots_shown: countDetails.slot_sum,
    })
  }

  const onMapShowHideButtonClick = (shouldMapLoad: boolean) => {
    const showMap = !shouldMapLoad
    setShouldMapLoad(showMap)
    !slotsLoading && logMapRudderstackEvent(showMap)
  }

  const coeInsuranceWidgetFlag = useGetUserTreatment(
    'COE_CAN_USE_INSURANCE_VERIFICATION_ON_SEARCH_PAGE'
  )
  const canSeeInsuranceVerificationWidget =
    isSupportUser && coeInsuranceWidgetFlag === treatmentFlagValues.ON

  return (
    <HeadComponent
      title="Thriveworks Counseling - Find and Book sessions with Providers"
      content="At Thriveworks find the best Providers at your preferred nearest location and book an online counselling or onsite therapy session"
      canonicalLink={canonicalLink}
    >
      <TimeZoneContext.Provider
        value={{
          timeZone,
          format,
          ...restTimeZone,
        }}
      >
        {canSeeInsuranceVerificationWidget ? (
          <>
            <SimpleInsuranceVerification
              isHideable={true}
              getInitialValuesFromSalesforce={true}
              setStateLocationOnSalesforceSync={true}
              styling={{
                // Because using position: fixed, left & bottom need to take padding & margin into account.
                containerClassName: '~fixed ~z-10 ~bottom-4 ~left-3',
              }}
              initialValues={{
                stateLocation: getParsedQuery(router)?.['state'],
                carrierId: getParsedQuery(router)?.['insuranceId'],
              }}
              overrideStateLocation={getParsedQuery(router)?.['state']}
              overrideCarrierId={getParsedQuery(router)?.['insuranceId']}
              onVerifyComplete={({ carrier }) => {
                updateProviderSearchQueryUrl({
                  router,
                  newQueries: { insuranceId: carrier.id },
                })
                sessionStorage.setItem(
                  sessionStorageKeys.INSURANCE_PROVIDER_NAME,
                  carrier.name
                )
              }}
            />
          </>
        ) : null}
        <InsuranceProvider>
          <MobileFilterModalProvider
            showMobileFilterModal={showMobileFilterModal}
            setShowMobileFilterModal={setShowMobileFilterModal}
          >
            <PageLoader
              loadingMessage=""
              loadingContainerClass="d-flex flex-fill h-100"
              isLoading={
                locationFetchLoading &&
                locationFetchStatus !== LOCATION_FETCH_STATUS.empty
              }
            >
              <div
                className={classNames(styles.search_page_container, {
                  [styles.search_page_container_support]: isSupportOrThirdPartyUser,
                })}
              >
                <div className={styles.header_container}>
                  <animated.div
                    style={headerPositionStyle}
                    className={styles.navbar_container}
                  >
                    <Navbar
                      navbarContainerStyle={classNames(
                        styles.searchpage_navbar,
                        {
                          [styles.dropdown_shadow]: !(
                            isMobileScreen || isLargeSize
                          ),
                        }
                      )}
                      hideBookSessionButton
                    >
                      {/* This filter is only displayed on desktop */}
                      {isDesktopView && (
                        <HeaderFilter groupedFilters={groupedFilters} />
                      )}
                    </Navbar>

                    <div className={styles.provider_filter_container}>
                      {/* Hiding the shadow on page scroll due to THRIV-3267, can come back so commenting this instead */}
                      {/* <div
                    className={classNames(
                      styles.sticky_container,
                      {
                        [styles.sticky_container_shadow]: pageScrolled,
                      },
                      'd-flex flex-column bg-white'
                    )}
                  > */}
                      {!isDesktopView && (
                        <div className="pb-3">
                          <HeaderFilter groupedFilters={groupedFilters} />
                        </div>
                      )}
                      {/* Filter container */}
                      <ScrollHorizontally
                        containerClass={classNames(
                          styles.overflow_unset_on_lg,
                          'w-100 mb-3 justify-content-center font-gibson'
                        )}
                        childrenClass={classNames(
                          styles.overflow_unset_on_lg,
                          'd-flex px-4 pb-2 pt-4'
                        )}
                      >
                        <ProviderFilter
                          groupedFilters={groupedFilters}
                          isInPersonSelected={isInPersonSelected}
                        />
                      </ScrollHorizontally>
                      {isSupportOrThirdPartyUser && <AvailabilityLegend />}
                    </div>
                  </animated.div>
                  <Prenav
                    callUsBannerNumber={{
                      number: phoneNumber,
                      formattedNumber: formattedPhoneNumber,
                    }}
                    containerStyle={classNames(styles.prenav_container, {
                      [styles.lower_zindex]: showMobileFilterModal,
                    })}
                  />
                </div>

                {showLocationModal && (
                  <ChangeLocationModal
                    handleSubmit={onChangeLocation}
                    groupedFilters={groupedFilters}
                    show={showLocationModal}
                    onHide={() => {
                      setShowLocationModal(false)
                    }}
                  />
                )}
                <div
                  ref={scrollContainerRef}
                  // Hiding the shadow on page scroll due to THRIV-3267, can come back so commenting this instead
                  // onScroll={onProviderListScroll}
                  className={styles.scrollable_content_section}
                >
                  <SlotDateContext.Provider
                    value={{
                      startSlotDate,
                      slotListLimit,
                      setStartSlotDate,
                      setSlotListLimit,
                    }}
                  >
                    <ProviderSlotContext.Provider
                      value={{
                        nextAvailabilityForProviders,
                        providerSlots,
                        loading: slotsLoading,
                        error: slotsError,
                      }}
                    >
                      {!isEmptyQuery ? (
                        <div className={styles.SearchList_container}>
                          <div
                            className={
                              [shouldMapLoad]
                                ? styles.SearchList_container_content_withMap
                                : styles.SearchList_container_content_withoutMap
                            }
                          >
                            <ProvidersList
                              scrollContainer={scrollContainerRef?.current}
                              groupedFilters={groupedFilters}
                              filterLookup={filterLookup}
                              providerListLoading={providerListLoading}
                              onSetProviderListLoading={setProviderListLoading}
                              providerList={providerList}
                              onSetProviderList={setProviderList}
                              onSetPracticesList={setPracticesList}
                              selectedPractice={selectedPractice}
                              onSelectPractice={setSelectedPractice}
                              providerPagination={providerPagination}
                              setProviderPagination={setProviderPagination}
                              shouldFetchPractices={
                                !!query.practiceId ||
                                (showMapOnDesktopScreen && shouldMapLoad)
                              }
                              providerCardDetails={providerCardDetails}
                              countDetails={countDetails}
                              inPersonFilterSelected={isInPersonSelected}
                              isWaterfallFlow={showWaterFallTreatment}
                              isZeroProviderState={isZeroProviderState}
                              isDirectMatch0WithOnlyPrimaryFilter={
                                isDirectMatch0WithOnlyPrimaryFilter
                              }
                              waterfallProviderInsuranceInfo={
                                waterfallProviderInsuranceInfo
                              }
                            />

                            {showWaterFallTreatment && (
                              <ProvidersListWaterfall
                                scrollContainer={scrollContainerRef?.current}
                                groupedFilters={groupedFilters}
                                filterLookup={filterLookup}
                                providerList={providerList}
                                selectedPractice={selectedPractice}
                                inPersonFilterSelected={isInPersonSelected}
                                countDetails={countDetails}
                                onSetIsZeroProviderState={
                                  setIsZeroProviderState
                                }
                                noWaterfallProviderAcceptsInsurance={
                                  waterfallProviderInsuranceInfo.noWaterfallProviderAcceptsInsurance
                                }
                                setNoWaterfallProviderAcceptsInsurance={
                                  setWaterfallProviderInsuranceInfo
                                }
                              />
                            )}
                          </div>

                          {shouldMapLoad
                            ? showMapOnDesktopScreen &&
                              providerList?.length > 0 && (
                                <div className="flex-grow-1">
                                  <PracticesMap
                                    practicesList={practicesList}
                                    selectedPractice={selectedPractice}
                                    shouldMapLoad={shouldMapLoad}
                                    onSelectPractice={(practice) => {
                                      setSelectedPractice(practice)
                                    }}
                                  />
                                </div>
                              )
                            : null}
                        </div>
                      ) : (
                        <div className="h-80"></div>
                      )}
                    </ProviderSlotContext.Provider>
                  </SlotDateContext.Provider>
                  <Footer />
                </div>
                {showMapOnDesktopScreen ? (
                  <ShowHideMapButton
                    shouldMapLoad={shouldMapLoad}
                    handleOnClick={() =>
                      onMapShowHideButtonClick(shouldMapLoad)
                    }
                    className={
                      isSupportOrThirdPartyUser && styles.addMarginBottom
                    }
                  />
                ) : null}
                {isSupportOrThirdPartyUser && <ResetSupportBooking />}
              </div>
            </PageLoader>
          </MobileFilterModalProvider>
        </InsuranceProvider>
      </TimeZoneContext.Provider>
    </HeadComponent>
  )
}

export default SearchPage
